import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { X, Send, Loader2, CheckCircle2, Mail, User, Phone, MessageSquare, AtSign } from "lucide-react";
import api from "@/lib/api";

/**
 * Public landing-page contact form.
 *
 * Rendered as a modal dialog with a soft backdrop and a scrollable card.
 * Fully i18n'd via the `landing.contact_form.*` keys; falls back to
 * English text through the `defaultValue` on every t() call so a missing
 * translation never surfaces a raw key to a visitor.
 *
 * Anti-spam
 * ---------
 * * A hidden honeypot field (`hp`) is included in the request body. Any
 *   value in it causes the backend to silently drop the message.
 * * The submit button locks itself while the request is in flight to
 *   prevent double-tap re-submission.
 * * Client-side length caps mirror the backend caps so an obvious
 *   invalid submit never even hits the network.
 */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const E164_RE = /^\+[1-9]\d{6,14}$/;

function normalisePhone(raw) {
  if (!raw) return "";
  const stripped = raw.replace(/[\s\-().]/g, "");
  if (!stripped) return "";
  return stripped.startsWith("+") ? stripped : "+" + stripped.replace(/^0+/, "");
}

export default function ContactModal({ open, onClose }) {
  const { t, i18n } = useTranslation();
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
    hp: "",
  });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const closeButtonRef = useRef(null);

  // Reset visible state whenever the modal is closed by the parent so
  // reopening feels fresh. We intentionally KEEP the last typed values
  // when the caller closes because of a validation error (per spec §4).
  useEffect(() => {
    if (!open) {
      // small timeout so the success screen stays visible during the
      // 300ms close animation before it wipes.
      const timer = setTimeout(() => {
        setDone(false);
        setErrors({});
      }, 250);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [open]);

  // Escape closes; focus trap kept minimal — we send focus to the close
  // button on open so keyboard users don't get stuck outside the dialog.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    // Prevent background scroll while the modal is open.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  const validate = () => {
    const next = {};
    if (form.full_name.trim().length < 2) next.full_name = t("landing.contact_form.err_name", { defaultValue: "Please enter your full name." });
    if (!EMAIL_RE.test(form.email.trim())) next.email = t("landing.contact_form.err_email", { defaultValue: "Enter a valid e-mail address." });
    if (form.phone.trim()) {
      const p = normalisePhone(form.phone);
      if (!E164_RE.test(p)) next.phone = t("landing.contact_form.err_phone", { defaultValue: "Use international format (e.g. +56961234567)." });
    }
    if (form.message.trim().length < 5) next.message = t("landing.contact_form.err_message", { defaultValue: "Please write a short message (5+ characters)." });
    if (form.message.length > 2000) next.message = t("landing.contact_form.err_message_long", { defaultValue: "Message is too long (max 2000 characters)." });
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!validate()) return;
    setBusy(true);
    try {
      await api.post("/contact/send", {
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() ? normalisePhone(form.phone) : null,
        subject: form.subject.trim() || null,
        message: form.message.trim(),
        language: i18n.language || "en",
        hp: form.hp,
      });
      setDone(true);
      // Clear the form so a second open starts blank.
      setForm({ full_name: "", email: "", phone: "", subject: "", message: "", hp: "" });
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail || t("landing.contact_form.err_send", { defaultValue: "We couldn't deliver your message. Please try again shortly." }));
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;
  const inputCls =
    "flex h-11 w-full rounded-lg border border-slate-300 bg-white pl-10 pr-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0F62FE]/60";
  const iconCls = "w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400";

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-3 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="contact-modal-title"
      data-testid="contact-modal"
    >
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
        data-testid="contact-modal-backdrop"
      />
      <div className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl bg-white shadow-2xl">
        <div className="sticky top-0 flex items-start justify-between gap-3 px-5 sm:px-6 pt-5 pb-3 bg-white border-b border-slate-100">
          <div>
            <h2
              id="contact-modal-title"
              className="text-xl sm:text-2xl font-extrabold text-slate-900"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              {t("landing.contact_form.title", { defaultValue: "Contact My GlobalTopup" })}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {t("landing.contact_form.subtitle", { defaultValue: "Send us a message and our team will get back to you shortly." })}
            </p>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-lg text-slate-500 hover:bg-slate-100"
            data-testid="contact-close"
            aria-label={t("landing.contact_form.close", { defaultValue: "Close" })}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {done ? (
          <div className="p-8 text-center" data-testid="contact-success">
            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <h3 className="mt-4 text-lg font-bold text-slate-900">
              {t("landing.contact_form.success_title", { defaultValue: "Message sent successfully" })}
            </h3>
            <p className="mt-2 text-sm text-slate-600">
              {t("landing.contact_form.success_body", { defaultValue: "Thank you for contacting My GlobalTopup. Our team will respond as soon as possible." })}
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-6 inline-flex items-center justify-center h-11 px-6 rounded-lg bg-[#0F62FE] text-white font-semibold hover:bg-[#0050E6]"
              data-testid="contact-success-close"
            >
              {t("landing.contact_form.done", { defaultValue: "Done" })}
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-5 sm:p-6 space-y-4" data-testid="contact-form" noValidate>
            {/* Honeypot — visually hidden but reachable to naive scrapers. */}
            <label className="hidden" aria-hidden="true">
              Company website
              <input
                tabIndex={-1}
                autoComplete="off"
                value={form.hp}
                onChange={(e) => setForm((f) => ({ ...f, hp: e.target.value }))}
              />
            </label>

            <ContactField
              label={t("landing.contact_form.field_name", { defaultValue: "Full name" })}
              error={errors.full_name}
              icon={<User className={iconCls} />}
              required
            >
              <input
                type="text"
                required
                autoFocus
                maxLength={120}
                value={form.full_name}
                onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
                className={inputCls}
                placeholder={t("landing.contact_form.ph_name", { defaultValue: "e.g. Jane Doe" })}
                data-testid="contact-full-name"
                autoComplete="name"
              />
            </ContactField>

            <ContactField
              label={t("landing.contact_form.field_email", { defaultValue: "E-mail address" })}
              error={errors.email}
              icon={<AtSign className={iconCls} />}
              required
            >
              <input
                type="email"
                required
                maxLength={254}
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                className={inputCls}
                placeholder="name@example.com"
                data-testid="contact-email"
                autoComplete="email"
              />
            </ContactField>

            <ContactField
              label={t("landing.contact_form.field_phone", { defaultValue: "Mobile phone (optional)" })}
              hint={t("landing.contact_form.hint_phone", { defaultValue: "International format, e.g. +56961234567" })}
              error={errors.phone}
              icon={<Phone className={iconCls} />}
            >
              <input
                type="tel"
                maxLength={32}
                value={form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                className={inputCls}
                placeholder="+56961234567"
                data-testid="contact-phone"
                autoComplete="tel"
              />
            </ContactField>

            <ContactField
              label={t("landing.contact_form.field_subject", { defaultValue: "Subject (optional)" })}
              icon={<Mail className={iconCls} />}
            >
              <input
                type="text"
                maxLength={200}
                value={form.subject}
                onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                className={inputCls}
                placeholder={t("landing.contact_form.ph_subject", { defaultValue: "How can we help?" })}
                data-testid="contact-subject"
              />
            </ContactField>

            <ContactField
              label={t("landing.contact_form.field_message", { defaultValue: "Message" })}
              error={errors.message}
              required
            >
              <div className="relative">
                <MessageSquare className={iconCls} style={{ top: 20 }} />
                <textarea
                  required
                  rows={5}
                  maxLength={2000}
                  value={form.message}
                  onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                  className="flex w-full rounded-lg border border-slate-300 bg-white pl-10 pr-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0F62FE]/60"
                  placeholder={t("landing.contact_form.ph_message", { defaultValue: "Tell us a bit about your business and how we can help." })}
                  data-testid="contact-message"
                />
                <div className="text-[10px] text-slate-400 mt-1 text-right">
                  {form.message.length} / 2000
                </div>
              </div>
            </ContactField>

            <div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-end pt-2">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center justify-center h-11 px-5 rounded-lg border border-slate-300 text-slate-700 text-sm font-semibold hover:bg-slate-50"
                data-testid="contact-cancel"
              >
                {t("landing.contact_form.cancel", { defaultValue: "Cancel" })}
              </button>
              <button
                type="submit"
                disabled={busy}
                className="inline-flex items-center justify-center gap-2 h-11 px-6 rounded-lg bg-[#0F62FE] text-white text-sm font-bold hover:bg-[#0050E6] disabled:opacity-60 disabled:cursor-not-allowed shadow-lg"
                data-testid="contact-submit"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {busy
                  ? t("landing.contact_form.sending", { defaultValue: "Sending…" })
                  : t("landing.contact_form.send", { defaultValue: "Send Message" })}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function ContactField({ label, hint, error, icon, required, children }) {
  return (
    <div>
      <label className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">
        {label}
        {required && <span className="text-rose-500 ml-1">*</span>}
      </label>
      <div className="relative">
        {icon}
        {children}
      </div>
      {hint && !error && <div className="text-[11px] text-slate-500 mt-1">{hint}</div>}
      {error && (
        <div className="text-[11px] text-rose-600 mt-1" data-testid="contact-field-error">
          {error}
        </div>
      )}
    </div>
  );
}
